import React, { useRef, useEffect, useState } from 'react';
import { motion, useScroll, useTransform, useInView, useReducedMotion, animate, useMotionValue, useSpring } from 'framer-motion';

export const EASE = [0.16, 1, 0.3, 1];

export const Reveal = ({ children, delay = 0, y = 18, className = '' }) => {
    const reduce = useReducedMotion();
    return (
        <motion.div
            className={className}
            initial={reduce ? { opacity: 0 } : { opacity: 0, y }}
            whileInView={reduce ? { opacity: 1 } : { opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.6, delay, ease: EASE }}
        >
            {children}
        </motion.div>
    );
};

export const Magnetic = ({ children, strength = 0.3, className = '' }) => {
    const ref = useRef(null);
    const reduce = useReducedMotion();
    const x = useMotionValue(0);
    const y = useMotionValue(0);
    const onMove = (e) => {
        if (reduce || !ref.current) return;
        const r = ref.current.getBoundingClientRect();
        x.set((e.clientX - (r.left + r.width / 2)) * strength);
        y.set((e.clientY - (r.top + r.height / 2)) * strength);
    };
    const onLeave = () => { x.set(0); y.set(0); };
    return (
        <motion.div ref={ref} onMouseMove={onMove} onMouseLeave={onLeave}
            style={{ x, y }} transition={{ type: 'spring', stiffness: 150, damping: 15, mass: 0.2 }}
            className={`inline-block ${className}`}>
            {children}
        </motion.div>
    );
};

export const Parallax = ({ children, offset = 40, className = '' }) => {
    const ref = useRef(null);
    const reduce = useReducedMotion();
    const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
    const y = useTransform(scrollYProgress, [0, 1], reduce ? [0, 0] : [offset, -offset]);
    return <motion.div ref={ref} style={{ y }} className={className}>{children}</motion.div>;
};

export const KineticText = ({ text, className = '', delay = 0 }) => {
    const reduce = useReducedMotion();
    if (reduce) return <span className={className}>{text}</span>;
    const words = String(text).split(' ');
    return (
        <span className={className}>
            {words.map((w, i) => (
                <span key={i} className="inline-block overflow-hidden align-bottom" style={{ paddingBottom: '0.06em' }}>
                    <motion.span className="inline-block" initial={{ y: '115%' }} animate={{ y: 0 }}
                        transition={{ delay: delay + i * 0.06, duration: 0.7, ease: EASE }}>
                        {w}&nbsp;
                    </motion.span>
                </span>
            ))}
        </span>
    );
};

export const CountUp = ({ to = 100, suffix = '', duration = 1.6, className = '' }) => {    const ref = useRef(null);
    const reduce = useReducedMotion();
    const inView = useInView(ref, { once: true, margin: '-40px' });
    const mv = useMotionValue(0);
    const [val, setVal] = useState(0);
    useEffect(() => {
        if (!inView) return;
        if (reduce) { setVal(to); return; }
        const controls = animate(mv, to, {
            duration,
            ease: EASE,
            onUpdate: (v) => setVal(Math.round(v)),
            onComplete: () => setVal(to),
        });
        return () => controls.stop();
    }, [inView, to, reduce]); // eslint-disable-line
    return <span ref={ref} className={className}>{val.toLocaleString('en-IN')}{suffix}</span>;
};

// Official XTRA logo lockup (real artwork). Blue mark on light backgrounds, yellow on dark.
export const Logo = ({ variant = 'light', className = '' }) => (
    <img
        src={variant === 'dark' ? '/xtra-mark-yellow.png' : '/xtra-mark-blue.png'}
        alt="XTRA"
        draggable="false"
        className={`w-auto object-contain select-none ${className}`}
    />
);

// Pointer-reactive 3D tilt for hero cards / stat panels (disabled for reduced motion + touch).
export const Tilt = ({ children, max = 8, className = '', style = {} }) => {
    const ref = useRef(null);
    const reduce = useReducedMotion();
    const rx = useMotionValue(0);
    const ry = useMotionValue(0);
    const srx = useSpring(rx, { stiffness: 150, damping: 16, mass: 0.3 });
    const sry = useSpring(ry, { stiffness: 150, damping: 16, mass: 0.3 });
    const onMove = (e) => {
        if (reduce || !ref.current) return;
        const r = ref.current.getBoundingClientRect();
        const px = (e.clientX - r.left) / r.width - 0.5;
        const py = (e.clientY - r.top) / r.height - 0.5;
        ry.set(px * max);
        rx.set(-py * max);
    };
    const onLeave = () => { rx.set(0); ry.set(0); };
    return (
        <motion.div
            ref={ref}
            onMouseMove={onMove}
            onMouseLeave={onLeave}
            style={{ rotateX: srx, rotateY: sry, transformPerspective: 900, transformStyle: 'preserve-3d', ...style }}
            className={className}
        >
            {children}
        </motion.div>
    );
};

// Smoothly animates a numeric value whenever it changes (great for live sliders).
export const AnimatedNumber = ({
    value = 0,
    format = (v) => Math.round(v).toLocaleString('en-IN'),
    className = '',
    ...rest
}) => {
    const reduce = useReducedMotion();
    const source = useMotionValue(value);
    const spring = useSpring(source, { stiffness: 140, damping: 22, mass: 0.4 });
    const [display, setDisplay] = useState(value);

    useEffect(() => {
        if (reduce) { source.jump(value); setDisplay(value); return; }
        source.set(value);
    }, [value, reduce, source]);

    useEffect(() => {
        if (reduce) return undefined;
        const unsub = spring.on('change', (v) => setDisplay(v));
        return () => unsub();
    }, [spring, reduce]);

    return <span className={className} {...rest}>{format(display)}</span>;
};
